import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { Position } from '@blueprintjs/core';
import { TimePrecision } from '@blueprintjs/datetime';
import moment from 'moment';
import get from 'lodash/get';

import { Flex, Box, Callout, Button, Text } from 'core/components';
import { timezone, DEFAULT_DATETIME_FORMAT, secondsIntervalToText, getDateRangeDisplay } from 'core/util/dateUtils';
import { bindFocusFirstItem } from 'core/util/ariaUtils';
import { KEY_NAMES } from 'core/util/keyCodes';
import DateRangeInputShortcuts from './DateRangeInputShortcuts';

const normalizeDate = (momentObj) => {
  const utc = timezone.value === 'UTC';
  const utcOffset = momentObj.utcOffset();

  return momentObj.subtract(utc ? utcOffset : 0, 'minute');
};

const fixDate = (date) => {
  const momentObj = moment(date);
  const utc = timezone.value === 'UTC';
  const utcOffset = momentObj.utcOffset();

  return momentObj.add(utc ? utcOffset : 0, 'minute');
};

const createDateRange = (lookbackSeconds) => {
  const now = moment();
  const start = normalizeDate(moment(now).subtract(lookbackSeconds, 'seconds'));
  const end = normalizeDate(moment(now));

  if (lookbackSeconds >= 1209600) {
    start.startOf('day');
    end.endOf('day');
  }

  return [start.toDate(), end.toDate()];
};

const setShortcutDateRanges = (shortcuts) =>
  shortcuts.map((shortcut) => {
    if (shortcut.dateRange) {
      return shortcut;
    }

    return {
      ...shortcut,
      dateRange: createDateRange(shortcut.lookbackSeconds)
    };
  });

// timezone is not set yet
export const DEFAULT_SHORTCUTS = [
  { label: 'Last 5 Minutes', lookbackSeconds: 300, includeTime: true },
  { label: 'Last 15 Minutes', lookbackSeconds: 900, includeTime: true },
  { label: 'Last Hour', lookbackSeconds: 3600, includeTime: true },
  { label: 'Last 3 Hours', lookbackSeconds: 10800, includeTime: true },
  { label: 'Last 6 Hours', lookbackSeconds: 21600, includeTime: true },
  { label: 'Last 1 Day', lookbackSeconds: 86400, includeTime: true },
  { label: 'Last 1 Week', lookbackSeconds: 604800, includeTime: true },
  { label: 'Last 2 Weeks', lookbackSeconds: 1209600, includeTime: true },
  { label: 'Last 30 Days', lookbackSeconds: 2592000, includeTime: true }
];

const DEFAULT_MIN_DATE = moment('2014-01-01').toDate();

class LookbackDateRange extends Component {
  static defaultProps = {
    format: 'YYYY-MM-DD HH:mm',
    timePrecision: TimePrecision.MINUTE
  };

  state = {
    isEditing: false,
    startDate: null,
    endDate: null,
    selectedShortcutIndex: -1,
    shortcuts: [],
    dirty: false,
    startDateInputFocused: false,
    endDateInputFocused: false
  };

  componentDidMount() {
    const { lookbackSeconds, minLookbackSeconds, maxLookbackSeconds, startDate, endDate } = this.props;

    // wait to create defaults until after timezone is set
    DEFAULT_SHORTCUTS.forEach((shortcut, i) => {
      DEFAULT_SHORTCUTS[i].dateRange = createDateRange(shortcut.lookbackSeconds);
    });

    const shortcuts = setShortcutDateRanges(this.getShortcuts(minLookbackSeconds, maxLookbackSeconds));
    const selectedShortcutIndex = this.findShortcut(shortcuts, lookbackSeconds);

    this.originalValues = { selectedShortcutIndex, startDate, endDate };
    this.setState({ selectedShortcutIndex, shortcuts });

    this.handleRef = this.handleRef.bind(this);
  }

  componentDidUpdate(prevProps, prevState) {
    const { lookbackSeconds, minLookbackSeconds, maxLookbackSeconds, onEditModeChange } = this.props;
    const { isEditing } = this.state;
    if (
      prevProps.lookbackSeconds !== lookbackSeconds ||
      prevProps.minLookbackSeconds !== minLookbackSeconds ||
      prevProps.maxLookbackSeconds !== maxLookbackSeconds
    ) {
      const shortcuts = setShortcutDateRanges(this.getShortcuts(minLookbackSeconds, maxLookbackSeconds));
      const selectedShortcutIndex = this.findShortcut(shortcuts, lookbackSeconds);
      Object.assign(this.originalValues, { selectedShortcutIndex });
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ selectedShortcutIndex, shortcuts });
    }

    if (prevState.isEditing !== isEditing && onEditModeChange) {
      onEditModeChange(isEditing);
    }
  }

  recalculateShortcuts = () => {
    const { minLookbackSeconds, maxLookbackSeconds } = this.props;
    const shortcuts = setShortcutDateRanges(this.getShortcuts(minLookbackSeconds, maxLookbackSeconds));

    this.setState({ shortcuts });
  };

  getShortcuts = (minLookbackSeconds, maxLookbackSeconds) => {
    const { overrideShortcuts } = this.props;

    let shortcuts = overrideShortcuts || DEFAULT_SHORTCUTS;

    if (minLookbackSeconds || maxLookbackSeconds) {
      shortcuts = shortcuts.filter(
        (shortcut) =>
          (!minLookbackSeconds || shortcut.lookbackSeconds >= minLookbackSeconds) &&
          (!maxLookbackSeconds || shortcut.lookbackSeconds <= maxLookbackSeconds)
      );
    }

    return shortcuts;
  };

  findShortcut = (shortcuts, lookbackSeconds) =>
    shortcuts.findIndex((shortcut) => shortcut.lookbackSeconds === lookbackSeconds);

  showDateRangeSelector = () => {
    this.setState({ isEditing: true, dirty: false });
  };

  hideDateRangeSelector = () => {
    this.setState({ isEditing: false, dirty: false });
  };

  cancelEditing = () => {
    this.setState({ isEditing: false, dirty: false, ...this.originalValues });
  };

  handleApplyClick = () => {
    const { onChange } = this.props;
    this.hideDateRangeSelector();

    const { startDate, endDate, shortcuts, selectedShortcutIndex } = this.state;
    if (onChange) {
      onChange({
        startDate,
        endDate,
        lookbackSeconds: selectedShortcutIndex === -1 ? null : shortcuts[selectedShortcutIndex].lookbackSeconds
      });
    }
  };

  handleRef = (ref) => {
    if (ref) {
      this.thisA11yRef = ref;
      // Use bindFocusFirstItem to create and bind the focus function to this instance
      this.focusFirstItem = bindFocusFirstItem(this, {
        selector: '[role="gridcell"][tabindex="0"]',
        container: this.thisA11yRef,
        delay: 0,
        onlyWhenEmpty: false
      });
    }
  };

  /**
   * Called when the user selects a day. If no days are selected, it will pass [null, null].
   * If a start date is selected but not an end date, it will pass [selectedDate, null].
   * If both a start and end date are selected, it will pass [startDate, endDate].
   */
  handleDateChange = (dateRange) => {
    const { adjustEndTime } = this.props;
    const [start, end] = dateRange;
    const startDate = fixDate(start).unix();
    let endDate = fixDate(end).unix();

    // adjust end time to be 23:59 instead of 00:00 and increment seconds by 59 in order to include the full time range for that day
    if (adjustEndTime && end) {
      const endMoment = moment(end);

      if (endMoment.hours() === 0 && endMoment.minutes() === 0) {
        endMoment.hours(23);
        endMoment.minutes(59);
      }

      endMoment.seconds(59);
      endDate = fixDate(endMoment).unix();
    }

    this.setState({
      startDate,
      endDate,
      selectedShortcutIndex: -1,
      dirty: true
    });
  };

  handleShortcutChange = (_shortcut, index) => {
    this.setState({ startDate: null, endDate: null, selectedShortcutIndex: index, dirty: true });
  };

  handleInputGroupKeyUp = (event) => {
    if (event.key === KEY_NAMES.ESCAPE) {
      this.cancelEditing();
    } else if (event.key === KEY_NAMES.DOWN || event.key === KEY_NAMES.F6) {
      this.focusFirstItem();
      event.preventDefault();
    }
  };

  handleInputFocus = (type) => {
    this.setState({ [`${type}InputFocused`]: true });
  };

  handleInputBlur = (type) => {
    this.setState({ [`${type}InputFocused`]: false });
  };

  getDateInputPlaceholders = () => {
    const { format = DEFAULT_DATETIME_FORMAT } = this.props;
    const { shortcuts, selectedShortcutIndex, startDateInputFocused, endDateInputFocused } = this.state;

    if (startDateInputFocused || endDateInputFocused) {
      const [startDate, endDate] = get(shortcuts[selectedShortcutIndex], 'dateRange') || [];

      if (startDateInputFocused && startDate) {
        return [moment(startDate).format(format), null];
      }

      if (endDateInputFocused && endDate) {
        return [null, moment(endDate).format(format)];
      }
    }

    return [null, null];
  };

  render() {
    const {
      disabled = false,
      dropdown,
      endDate,
      format = DEFAULT_DATETIME_FORMAT,
      maxDate,
      minDate,
      /*
        when minimal is true:

        - the 'Time Range' label will not show
        - start/end date inputs are shortened to their absolute minimum
        - the 'Apply' and 'Cancel' buttons are shortened to their absolute minimum
      */
      minimal = false,
      showLabel = true,
      showShortcutLabelAsInterval,
      showShortcuts = true,
      stacked = false,
      startDate
    } = this.props;
    const {
      dirty,
      isEditing,
      selectedShortcutIndex,
      shortcuts,
      startDate: stateStartDate,
      endDate: stateEndDate
    } = this.state;

    const defaultValue = [
      startDate ? normalizeDate(moment.unix(startDate)).toDate() : startDate,
      endDate ? normalizeDate(moment.unix(endDate)).toDate() : endDate
    ];
    const selectedShortcut = shortcuts[selectedShortcutIndex];
    const dropdownOffset = dropdown ? '197, 5' : '194, 6';

    const [startDatePlaceholder, endDatePlaceholder] = this.getDateInputPlaceholders();
    const now = moment();

    const textProps = {
      as: 'div',
      small: !stacked,
      fontWeight: stacked ? 'normal' : 'bold',
      whiteSpace: 'nowrap'
    };

    let dateRangeDisplay = '';
    if (selectedShortcutIndex === -1) {
      dateRangeDisplay = getDateRangeDisplay(startDate, endDate);
    } else {
      dateRangeDisplay = showShortcutLabelAsInterval
        ? selectedShortcut?.label
        : `Last ${secondsIntervalToText(shortcuts[selectedShortcutIndex].lookbackSeconds)}`;
    }

    const selectButton = (
      <Box>
        <span id="look-back-button-screen-reader-instructions" style={{ display: 'none' }}>
          Enter or space to Edit
        </span>
        <Button
          disabled={disabled}
          onClick={this.showDateRangeSelector}
          textAlign="left"
          rightIcon="caret-down"
          height={stacked ? 30 : 35}
          width={stacked ? '100%' : 'auto'}
          aria-describedby="look-back-button-screen-reader-instructions"
        >
          {!stacked && (
            <Text as="div" small color="muted" mb="1px">
              Time Range ({timezone.value})
            </Text>
          )}

          {selectedShortcutIndex !== -1 && (
            <Text aria-label={dateRangeDisplay} {...textProps}>
              {dateRangeDisplay}
            </Text>
          )}

          {selectedShortcutIndex === -1 && (
            <Text aria-label={dateRangeDisplay} {...textProps}>
              {dateRangeDisplay}
            </Text>
          )}
        </Button>
      </Box>
    );

    const editorInputStyle = {};
    let editorButtonWidth = stacked ? '50%' : '90px';

    if (minimal) {
      editorInputStyle.width = 120;
      editorButtonWidth = 50;
    }

    const editor = (
      <Callout
        ml={dropdown || stacked || minimal ? 0 : 1}
        bg={dropdown ? 'unset' : 'auto'}
        width={stacked || minimal ? 'auto' : 567}
        minWidth={stacked || minimal ? 150 : 567}
        height={stacked ? 120 : 35}
        p={stacked ? 1 : '6px'}
        whiteSpace="nowrap"
        position="relative"
        zIndex="101" // 1 higher than highcharts tooltip
        ref={this.handleRef}
      >
        <Flex alignItems="center">
          {showLabel && !minimal && (
            <Text as="div" small color="muted" mr={1} textAlign="left">
              Time Range
            </Text>
          )}
          <Flex flexDirection={stacked ? 'column' : 'row'}>
            <Flex flexWrap="wrap">
              <span id="look-back-input-screen-reader-instructions" style={{ display: 'none' }}>
                {/* // Save this version for when the popover is truely Screen Reader Friendly:
                    Down arrow or F6 to enter date range popover. Tab to navigate through date range options */}
                Date Range format is four digit year, two digit month, two digit day, (space) two digit hour, two digit
                minute. Date values seperated by dashes, time values seperated by colons. Hour and minute default to 00
                if not specified.
              </span>
              <DateRangeInputShortcuts
                stacked={stacked}
                closeOnSelection={stacked}
                formatDate={(date) => moment(date).format(format)}
                onChange={this.handleDateChange}
                onShortcutChange={this.handleShortcutChange}
                parseDate={(str) => moment(str, format).toDate()}
                selectedShortcutIndex={selectedShortcutIndex}
                highlightCurrentDay
                flexWrap="wrap"
                startInputProps={{
                  style: { flex: '0 0 100% !important', flexWrap: 'wrap', ...editorInputStyle },
                  autoFocus: isEditing,
                  small: !stacked,
                  onKeyUp: this.handleInputGroupKeyUp,
                  onFocus: this.handleInputFocus.bind(null, 'startDate'),
                  onBlur: this.handleInputBlur.bind(null, 'startDate'),
                  placeholder: startDatePlaceholder,
                  'aria-describedby': 'look-back-input-screen-reader-instructions',
                  'aria-live': 'polite'
                }}
                endInputProps={{
                  style: editorInputStyle,
                  small: !stacked,
                  onKeyUp: this.handleInputGroupKeyUp,
                  onFocus: this.handleInputFocus.bind(null, 'endDate'),
                  onBlur: this.handleInputBlur.bind(null, 'endDate'),
                  placeholder: endDatePlaceholder,
                  'aria-describedby': 'look-back-input-screen-reader-instructions',
                  'aria-live': 'polite'
                }}
                shortcuts={showShortcuts && shortcuts}
                allowSingleDayRange
                defaultValue={defaultValue}
                minDate={minDate || DEFAULT_MIN_DATE}
                maxDate={maxDate || normalizeDate(moment(now).subtract(-60, 'minute')).toDate()}
                selectAllOnFocus={false} // PROBABLY
                popoverProps={{
                  minimal: true,
                  modifiers: {
                    flip: { enabled: true, boundariesElement: 'viewport' },
                    preventOverflow: { enabled: true, boundariesElement: 'window' },
                    offset: { enabled: true, offset: showShortcuts ? dropdownOffset : '0, 5' }
                  },
                  position: Position.BOTTOM_RIGHT,
                  targetTagName: 'div',
                  wrapperTagName: 'div',
                  onClosing: () => {
                    if (!dirty) {
                      this.setState((prevState) => ({ isEditing: !prevState.isEditing }));
                    }
                  },
                  onOpening: () => {
                    this.recalculateShortcuts();
                  }
                  // Taking this out in Release but we have a CSS fix brewing for Dev
                  // usePortal: false // VPAT: usePortal is not accessible, so we disable it for now
                }}
              />
            </Flex>
            <Box>
              <Flex justifyContent="space-between" gap={stacked ? 1 : '4px'} px={stacked ? 0 : 1} py={stacked ? 1 : 0}>
                <Button
                  small={!stacked}
                  onClick={this.handleApplyClick}
                  text="Apply"
                  width={editorButtonWidth}
                  disabled={selectedShortcutIndex < 0 && (!stateStartDate || !stateEndDate)}
                  title="Apply"
                />
                <Button
                  small={!stacked}
                  onClick={this.cancelEditing}
                  text="Cancel"
                  width={editorButtonWidth}
                  title="Cancel"
                />
              </Flex>
            </Box>
          </Flex>
        </Flex>
      </Callout>
    );

    if (dropdown) {
      return (
        <Box position="relative">
          {selectButton}
          {isEditing && (
            <Box
              position="absolute"
              top={35}
              left={1}
              zIndex={2147483647}
              width={dropdown ? 591 : 596}
              height={36}
              border="thin"
              bg="subnavBackground"
              pl={dropdown ? '20px' : '29px'}
              borderRadius="4px"
            >
              {editor}
            </Box>
          )}
        </Box>
      );
    }

    if (!isEditing) {
      return selectButton;
    }

    return editor;
  }
}

export default observer(LookbackDateRange);
